import { memoizedGroupBySelector } from '@travelnest/entities';
import { useDataLoad } from '@travelnest/merlin-hooks';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IProperty } from '../../../data/entities/property';
import { actions as entityActions } from '../../../data/store';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';

/**
 * Retrieve a customer's property list (filtering out inactive and unpublished properties), and a loading state.
 * @param customerId {string} - The customer's ID to fetch properties for.
 */
export const useCustomersProperties = (customerId: string) => {
	const dispatch = (useDispatch() as IThunkDispatch);
	const propertiesInStoreForCustomer = useSelector(
		(state: IState) => memoizedGroupBySelector('owner_id', state.data.properties.byId)[customerId] || []
	);
	const loading = useDataLoad(
		propertiesInStoreForCustomer.length === 0,
		async () => await dispatch(entityActions.property.getByOwnerId(customerId))
	);
	const customerProperties = propertiesInStoreForCustomer
		.filter((p: IProperty) => p.is_active && !!p.published_at);
	return { customerProperties, loading };
};

export const useCustomersPropertyScores = (customerId: string, forceLoad?: boolean) => {
	const dispatch = (useDispatch() as IThunkDispatch);
	const propertyScores = useSelector((state: IState) => state.data.propertyScores.byOwnerId[customerId]);
	const loading = useDataLoad(
		propertyScores === undefined || !!forceLoad,
		async () => await dispatch(entityActions.propertyScores.getByOwnerId(customerId))
	);
	const sortedPropertyScores = propertyScores?.sort((a, b) => a.total_score - b.total_score);

	return { propertyScores: sortedPropertyScores || [], loading };
};
export const useCustomersSelectedPropertyScore = (propertyId: number) => {
	const dispatch = (useDispatch() as IThunkDispatch);
	const selectedPropertyScore = useSelector((state: IState) => state.data.propertyScores.byPropertyId[propertyId]);
	const loading = useDataLoad(
		true,
		async () => await dispatch(entityActions.propertyScores.getByPropertyId(propertyId))
	);
	const [scoreDiff, setScoreDiff] = React.useState<number>(0);

	React.useEffect(() => {
		if (selectedPropertyScore) {
			const previousScore = sessionStorage.getItem(`${selectedPropertyScore.property_id}-current-property-score`);
			if (previousScore === null) {
				sessionStorage.setItem(`${selectedPropertyScore.property_id}-current-property-score`, `${selectedPropertyScore.score}`);
			} else {
				setScoreDiff(selectedPropertyScore.score - Number(previousScore));
				sessionStorage.setItem(`${selectedPropertyScore.property_id}-current-property-score`, `${selectedPropertyScore.score}`);
			}
		}
	}, [selectedPropertyScore]);

	return { selectedPropertyScore, loading, scoreDiff };
};

export const useScrollToTop = () => {
	React.useEffect(() => {
		const rootEl = document.getElementById('root');
		const rootElXPos = rootEl?.getBoundingClientRect().x;
		window.scroll(rootElXPos!, 0);
	}, []);
};
